import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button, IButtonProps } from '@/common/components/Button/Button';
import { Heading } from '@/common/components/Heading/Heading';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { maybeNavigateBack } from '@/common/utils/router';
import { useRouter } from 'expo-router';
import { styles } from './PageHeader.styles';

export type TPageHeaderProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  primaryAction?: ReactElement<IButtonProps | object> | null;
  infoButton?: Omit<IButtonProps, 'variant'>;
  backButton?: boolean;
  actions?: React.ReactNode;
};

export const PageHeader = ({
  title,
  description,
  primaryAction,
  infoButton,
  backButton = false,
  actions,
}: TPageHeaderProps) => {
  const { t } = useTranslation();
  const breakpoints = useBreakpoints(['lg']);
  const router = useRouter();

  return (
    <Stack className={styles.root}>
      {breakpoints.lg && backButton && (
        <Button
          variant="tertiary"
          size="sm"
          onPress={() => maybeNavigateBack(router)}
          icon={<Icon icon="arrow-left-long" />}
          className={styles.backButton}
        >
          {t('general.back_label')}
        </Button>
      )}
      <Stack horizontal justify="between" align="center" gap="md">
        <Heading level={1}>{title}</Heading>
        <Stack horizontal gap="md" align="center" shrink={false}>
          {infoButton && (
            <Button
              icon={<Icon icon={['far', 'circle-info']} />}
              {...infoButton}
              variant="info"
              size={breakpoints.lg ? 'md' : 'sm'}
            />
          )}
          {breakpoints.lg && primaryAction}
          {actions}
        </Stack>
      </Stack>
      {description && (
        <BodyText
          className={styles.description}
          size="sm"
          color="neutral20"
          context="provider"
        >
          {description}
        </BodyText>
      )}
    </Stack>
  );
};
